import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosConfig";
import { toast } from "react-toastify";

const EditPricingPopup = ({ moduleId, onClose }) => {
  const [moduleDetails, setModuleDetails] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userPlans, setUserPlans] = useState([]);
  const [moduleFeatureAccessCodes, setModuleFeatureAccessCodes] = useState({});
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [planValues, setPlanValues] = useState({});
  const [priceAllocatedFrom, setPriceAllocatedFrom] = useState("");
  const [priceAllocatedTo, setPriceAllocatedTo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchModuleDetails = async () => {
      if (!moduleId) return;
      try {
        const response = await axiosInstance.get(`/modules/${moduleId}`);
        setModuleDetails(response.data.data);
        const modulesData = response.data.data;
        const featureAccessCodes = {};
        modulesData.forEach((module) => {
          if (module.moduleFeatureAccessIds) {
            featureAccessCodes[module.moduleId] =
              module.moduleFeatureAccessIds.map((fa) => ({
                id: fa.moduleFeatureAccessId,
                code: fa.featureAccessCode,
                name: fa.featureName,
              }));
          }
        });
        setModuleFeatureAccessCodes(featureAccessCodes);
      } catch (error) {
        console.error("Error fetching module details:", error);
      }
    };
    fetchModuleDetails();
  }, [moduleId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, userPlansRes, pricingRes, modulesRes] =
          await Promise.all([
            axiosInstance.get("/subscription-plans"),
            axiosInstance.get("/user-plans"),
            axiosInstance.get(`/module-pricing-plans/${moduleId}`),
            axiosInstance.get("/modules"),
          ]);
        if (plansRes.data.status === 200)
          setSubscriptionPlans(plansRes.data.data);
        if (userPlansRes.data.status === 200)
          setUserPlans(userPlansRes.data.data);
        if (pricingRes.data) {
          const { pricingDetails } = pricingRes.data;
          if (pricingDetails?.userPlans) setUserPlans(pricingDetails.userPlans);
        }
        if (modulesRes.data.status === 200) {
          const featureAccessCodes = {};
          modulesRes.data.data.forEach((module) => {
            if (module.moduleFeatureAccessIds) {
              featureAccessCodes[module.moduleId] =
                module.moduleFeatureAccessIds.map((fa) => ({
                  id: fa.moduleFeatureAccessId,
                  code: fa.featureAccessCode,
                  name: fa.featureName,
                }));
            }
          });
          setModuleFeatureAccessCodes(featureAccessCodes);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [moduleId]);

  const handleUserPlanValueChange = (planId, userPlanId, value) => {
    const sanitizedValue = value === "" ? "" : Number(value);
    setPlanValues((prev) => ({
      ...prev,
      [planId]: { ...prev[planId], [userPlanId]: sanitizedValue },
    }));
  };

  const toggleFeature = (planId, featureId, type) => {
    setSelectedFeatures((prev) => {
      const key =
        type === "moduleFeature"
          ? "moduleFeatureIds"
          : "moduleFeatureAccessIds";
      const current = prev[planId]?.[key] || [];
      return {
        ...prev,
        [planId]: {
          ...prev[planId],
          [key]: current.includes(featureId)
            ? current.filter((id) => id !== featureId)
            : [...current, featureId],
        },
      };
    });
  };

  const toggleSelectAllFeatures = (planId) => {
    const allIds =
      moduleDetails?.moduleFeatures?.map((f) => f.moduleFeatureId) || [];
    setSelectedFeatures((prev) => {
      const current = prev[planId]?.moduleFeatureIds || [];
      return {
        ...prev,
        [planId]: {
          ...prev[planId],
          moduleFeatureIds: current.length === allIds.length ? [] : allIds,
          moduleFeatureAccessIds: prev[planId]?.moduleFeatureAccessIds || [],
        },
      };
    });
  };

  const toggleSelectAllAccessCodes = (planId) => {
    const allIds = moduleFeatureAccessCodes[moduleId]?.map((a) => a.id) || [];
    setSelectedFeatures((prev) => {
      const current = prev[planId]?.moduleFeatureAccessIds || [];
      return {
        ...prev,
        [planId]: {
          ...prev[planId],
          moduleFeatureIds: prev[planId]?.moduleFeatureIds || [],
          moduleFeatureAccessIds:
            current.length === allIds.length ? [] : allIds,
        },
      };
    });
  };

  const copyPricingToAll = (planId, sourceUserPlanId) => {
    const base = planValues[planId] || {};
    const amount = base[sourceUserPlanId] || "";
    const admin = base[`admin-${sourceUserPlanId}`] || "";
    const server = base[`server-${sourceUserPlanId}`] || "";
    userPlans.forEach((up) => {
      handleUserPlanValueChange(planId, up.userPlanId, amount);
      handleUserPlanValueChange(planId, `admin-${up.userPlanId}`, admin);
      handleUserPlanValueChange(planId, `server-${up.userPlanId}`, server);
    });
  };

  const savePricingData = async () => {
    setLoading(true);
    const pricingData = {
      moduleId,
      subscriptionPlans: Object.keys(selectedFeatures).map((planName) => {
        const subscriptionPlan = subscriptionPlans.find(
          (p) => p.planName === planName,
        );
        return {
          subscriptionPlanId: subscriptionPlan?.planId,
          planName,
          planSequence: subscriptionPlan?.planSequence || 0,
          userPlans: userPlans.map((up) => ({
            userPlanId: up.userPlanId,
            minUser: up.minimumUser,
            maxUser: up.maximumUser,
            planAmount: planValues[planName]?.[up.userPlanId] || 0,
            planAmountPerAdmin:
              planValues[planName]?.[`admin-${up.userPlanId}`] || 0,
            serverCostPercenatgePerUser:
              planValues[planName]?.[`server-${up.userPlanId}`] || 0,
            priceAllocatedFrom,
            priceAllocatedTo,
          })),
          moduleFeatureIDs: selectedFeatures[planName]?.moduleFeatureIds || [],
          moduleFeatureAccessIDs:
            selectedFeatures[planName]?.moduleFeatureAccessIds || [],
        };
      }),
      priceAllocatedFrom,
      priceAllocatedTo,
    };
    try {
      const response = await axiosInstance.put(
        "/module-pricing-plans/update",
        pricingData,
      );
      if (response.status === 200) {
        toast.success("Pricing plan updated successfully!");
        onClose();
      }
    } catch (error) {
      console.error("Error updating pricing plan:", error);
      toast.error("Failed to update pricing plan.");
    } finally {
      setLoading(false);
    }
  };

  const availableFeatures = moduleDetails?.moduleFeatures || [];
  const availableAccessCodes = moduleFeatureAccessCodes[moduleId] || [];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">Edit Pricing Plan</h2>
            {moduleDetails?.moduleName && (
              <span className="bg-white/20 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                {moduleDetails.moduleName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {moduleDetails && (
            <div className="p-4 space-y-4">
              {/* Row 1: Date Range */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                    Price From
                  </label>
                  <input
                    type="date"
                    value={priceAllocatedFrom}
                    onChange={(e) => setPriceAllocatedFrom(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                    Price To
                  </label>
                  <input
                    type="date"
                    value={priceAllocatedTo}
                    onChange={(e) => setPriceAllocatedTo(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Row 2: Subscription Plans */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1.5">
                  Subscription Plan
                </label>
                <div className="flex flex-wrap gap-2">
                  {subscriptionPlans.length > 0 ? (
                    subscriptionPlans.map((plan) => (
                      <button
                        key={plan.planId}
                        onClick={() => setSelectedPlanId(plan.planName)}
                        className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium transition-all ${
                          selectedPlanId === plan.planName
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 bg-white hover:border-gray-300 text-gray-600"
                        }`}
                      >
                        {plan.planName}
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">
                      No subscription plans available
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3: Features + Access Codes + User Plans — 3 columns */}
              {selectedPlanId && (
                <div className="grid grid-cols-3 gap-4 min-h-0">
                  {/* Module Features */}
                  <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                      <span className="text-xs font-bold text-gray-700 tracking-wide">
                        Module Features
                      </span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 text-blue-600 rounded"
                          checked={
                            availableFeatures.length > 0 &&
                            selectedFeatures[selectedPlanId]?.moduleFeatureIds
                              ?.length === availableFeatures.length
                          }
                          onChange={() =>
                            toggleSelectAllFeatures(selectedPlanId)
                          }
                        />
                        <span className="text-xs text-gray-500">All</span>
                      </label>
                    </div>
                    <div className="overflow-y-auto p-2 flex-1">
                      <div className="grid grid-cols-3 gap-1">
                        {availableFeatures.map((feature) => (
                          <label
                            key={feature.moduleFeatureId}
                            className="flex items-start gap-1.5 p-1.5 rounded-md hover:bg-white cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5 w-3.5 h-3.5 text-blue-600 rounded flex-shrink-0"
                              checked={selectedFeatures[
                                selectedPlanId
                              ]?.moduleFeatureIds?.includes(
                                feature.moduleFeatureId,
                              )}
                              onChange={() =>
                                toggleFeature(
                                  selectedPlanId,
                                  feature.moduleFeatureId,
                                  "moduleFeature",
                                )
                              }
                            />
                            <p
                              className="text-xs font-medium text-gray-800 leading-snug truncate"
                              title={feature.featureName}
                            >
                              {feature.featureName}
                            </p>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Feature Access Codes */}
                  <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                      <span className="text-xs font-bold text-gray-700 tracking-wide">
                        Access Codes
                      </span>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 text-purple-600 rounded"
                          checked={
                            availableAccessCodes.length > 0 &&
                            selectedFeatures[selectedPlanId]
                              ?.moduleFeatureAccessIds?.length ===
                              availableAccessCodes.length
                          }
                          onChange={() =>
                            toggleSelectAllAccessCodes(selectedPlanId)
                          }
                        />
                        <span className="text-xs text-gray-500">All</span>
                      </label>
                    </div>
                    <div className="overflow-y-auto p-2 space-y-1 flex-1">
                      {availableAccessCodes.map((access) => (
                        <label
                          key={access.id}
                          className="flex items-center gap-2 p-2 rounded-md hover:bg-white cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 text-purple-600 rounded flex-shrink-0"
                            checked={selectedFeatures[
                              selectedPlanId
                            ]?.moduleFeatureAccessIds?.includes(access.id)}
                            onChange={() =>
                              toggleFeature(
                                selectedPlanId,
                                access.id,
                                "moduleFeatureAccess",
                              )
                            }
                          />
                          <span className="text-xs font-medium text-gray-700 font-mono">
                            {access.code}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* User Plans Pricing */}
                  <div className="bg-gray-50 rounded-lg border border-gray-200 flex flex-col">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <span className="text-xs font-bold text-gray-700 tracking-wide">
                        User Plans — {selectedPlanId}
                      </span>
                    </div>
                    <div className="overflow-y-auto p-2 space-y-2 flex-1">
                      {userPlans.length > 0 ? (
                        userPlans.map((userPlan) => (
                          <div
                            key={userPlan.userPlanId}
                            className="bg-white rounded-lg border border-gray-200 p-2"
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-semibold text-gray-700">
                                {userPlan.minimumUser}–{userPlan.maximumUser}{" "}
                                Users
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  copyPricingToAll(
                                    selectedPlanId,
                                    userPlan.userPlanId,
                                  )
                                }
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                              >
                                Copy all
                              </button>
                            </div>
                            <div className="grid grid-cols-3 gap-1.5">
                              <div>
                                <label className="block text-xs text-gray-400 mb-0.5">
                                  Amount (₹)
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={
                                    planValues[selectedPlanId]?.[
                                      userPlan.userPlanId
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    handleUserPlanValueChange(
                                      selectedPlanId,
                                      userPlan.userPlanId,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-0.5">
                                  Per Admin (₹)
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={
                                    planValues[selectedPlanId]?.[
                                      `admin-${userPlan.userPlanId}`
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    handleUserPlanValueChange(
                                      selectedPlanId,
                                      `admin-${userPlan.userPlanId}`,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-0.5">
                                  Server %
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={
                                    planValues[selectedPlanId]?.[
                                      `server-${userPlan.userPlanId}`
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    handleUserPlanValueChange(
                                      selectedPlanId,
                                      `server-${userPlan.userPlanId}`,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-xs text-gray-400">
                            No user plans available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0 rounded-b-xl bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={savePricingData}
            disabled={loading}
            className={`px-5 py-1.5 text-sm rounded-lg font-semibold transition-all text-white flex items-center gap-2 ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
            }`}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-3.5 w-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPricingPopup;
